import { retryImport } from "@/shared";
import ErrorBoundary from "@components/error-boundary";
import type { WholeStoryType } from "@config";
import { cn } from "@lib/cn";
import { useLoadStory } from "@pages/stories/hooks/use-load-story";
import {
  Suspense,
  lazy,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";

const Story = lazy(() =>
  retryImport(() => import("@/pages/editor/modules/static/components/Story")),
);

export type DocumentPreviewProps = {
  contentPointer: string | undefined;
  previewUrl: string | undefined;
  ogImage?: string;
  showOutline?: boolean;
  forceIframe?: boolean;
  /** Keep the preview mounted after it first became visible. Without this,
   *  scrolling it out of view unmounts the iframe and scrolling back
   *  reloads it — right for a grid of dozens of cards, wrong for a single
   *  always-relevant preview (chrome-extension panel). */
  keepMounted?: boolean;
};

const DocumentGridPreviewComponent = ({
  contentPointer,
  previewUrl,
  ogImage,
  showOutline = false,
  forceIframe = false,
  keepMounted = false,
}: DocumentPreviewProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState<number>(0);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const { ref, story, isIntersecting } = useLoadStory(
    contentPointer,
    containerRef,
    Boolean(ogImage || !contentPointer || forceIframe),
  );

  // With keepMounted, latch visibility: lazy-load on first intersection,
  // then never unmount (an iframe remount is a full reload).
  const [hasIntersected, setHasIntersected] = useState(false);
  useEffect(() => {
    if (keepMounted && isIntersecting) setHasIntersected(true);
  }, [keepMounted, isIntersecting]);
  const visible = isIntersecting || (keepMounted && hasIntersected);

  const updateHeight = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    setContainerHeight(container.clientHeight);
    setContainerWidth(container.clientWidth);
  }, []);

  const debouncedUpdateHeight = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      updateHeight();
    }, 500);
  }, [updateHeight]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Initial measurement (immediate, no debounce)
    updateHeight();

    // Use ResizeObserver to track height changes with debouncing
    const resizeObserver = new ResizeObserver(debouncedUpdateHeight);

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [updateHeight, debouncedUpdateHeight]);

  const content = useMemo(() => {
    if (ogImage) {
      return (
        <img
          src={ogImage}
          alt="Storydoc"
          className="tw-size-full tw-object-cover"
        />
      );
    }

    if (story === null && visible) {
      return <IframePreview previewUrl={previewUrl} />;
    }
    if (story && visible) return <ContentPreview story={story} />;

    return (
      <span className="tw-block tw-size-full tw-animate-skeleton-loading tw-bg-blue-50" />
    );
  }, [story, ogImage, visible, previewUrl]);

  return (
    <div
      style={
        {
          "--container-height": `${containerHeight + 7}px`,
          // Scale the fixed 1920px-wide iframe to fill the container at any
          // width. No upper cap: grid cards stay well under 1.0, while the
          // full-width chrome-extension panel can exceed 480px and must keep
          // scaling up instead of freezing at the old 0.25 ceiling.
          "--iframe-scale": containerWidth / 1920,
        } as CSSProperties
      }
      className={cn(
        // 12px corners and a blue/5 hairline, per node 58:74803.
        "stories-page-story-grid-preview tw-aspect-video tw-overflow-hidden tw-rounded-xl tw-bg-white tw-outline tw-outline-1 tw-outline-blue/5",
        showOutline ? "tw-border-primary" : "tw-border-blue/5",
      )}
      ref={(r) => {
        if (!r) return;
        containerRef.current = r;
        ref?.(r);
      }}
    >
      <div
        className={cn(
          "tw-transition-opacity tw-duration-200",
          visible ? "tw-opacity-100" : "tw-opacity-0",
          ogImage && "tw-h-full",
        )}
      >
        {content}
      </div>
    </div>
  );
};

export const DocumentGridPreview = memo<DocumentPreviewProps>(
  DocumentGridPreviewComponent,
);

const IframePreview = ({ previewUrl }: { previewUrl: string | undefined }) => {
  return (
    <iframe
      src={`${previewUrl}?preview=false&analytics=false&progressBar=false&engagementBar=false&loader=false&static=true&selected-slides=2`}
      title="Story preview"
      className="stories-page-story-preview-iframe tw-pointer-events-none"
    />
  );
};

const ContentPreview = ({ story }: { story: WholeStoryType }) => {
  return (
    <ErrorBoundary
      feature="Story preview"
      fallback={<IframePreview previewUrl={story.previewUrl} />}
    >
      <Suspense
        fallback={
          <span className="tw-block tw-size-full tw-animate-skeleton-loading tw-bg-blue-50" />
        }
      >
        <div className="stories-page-story-preview-content">
          <Story story={story} />
        </div>
      </Suspense>
    </ErrorBoundary>
  );
};
